import {  PlusCircleOutlined } from '@ant-design/icons';
import { Button, Modal, Input, message, Spin, Select } from 'antd';
import { observer } from 'mobx-react';
import React, { FC, useState, useEffect } from 'react';
import CloseLine from '@/assets/images/icons/close-line.react.svg?react';
import CreateTapdEditDisable from '@/assets/images/public/create-tapd-edit-disable.svg';
import CreateTapdEdit from '@/assets/images/public/create-tapd-edit.svg';
import { getUserId } from '@/infrastructure/auth';
import { getProductId } from '@/infrastructure/product-info';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import { storageSet, storageGet } from '@/infrastructure/storage-utils';
import { ModalIconType, ModalMsg, ModalType } from '@/presentation/components/modal-msg';
import {
  getInitFields,
  initReporterData,
  initCurrentOwnerData,
  initVersionReportData,
} from '@/presentation/components/tapd-info';
import {
  getIssueTapdInfos,
  createTapdIssue,
  getCustomFields,
  CreateTapdIssueReq,
} from '@/presentation/components/tapd-info/api';
import { RenderAutoComplete } from '@/presentation/components/tapd-info/create-tapd-modal/auto-complete';
import { RenderInput } from '@/presentation/components/tapd-info/create-tapd-modal/input';
import { RenderSelect } from '@/presentation/components/tapd-info/create-tapd-modal/select';
import { RenderUserSelect } from '@/presentation/components/tapd-info/create-tapd-modal/user-select';
import { EditType, formatTapdInfo } from '@/presentation/components/tapd-info/edit-tapd-modal';
import { deleteIssueTapdInfos } from '@/presentation/components/tapd/api';
import styles from '@/presentation/components/tapd/create-tapd-modal/index.module.scss';
import { getRoleByUser } from '@/presentation/components/user-manage/api';
import { useStores } from '@/presentation/store';
import { FilterOptions } from '@/proto/alert';
import { Zone, Option } from '@/proto/field';
import { getTypeByZone } from '@/proto/issue';
import { ITapdTemplateInfos } from '@/proto/manage';
import { useTranslation } from 'react-i18next';

export interface FieldsType {
  name: string | null | undefined;
  label: string | null | undefined;
  value: string | number | null | undefined;
  type: string | null | undefined;
  options?: FilterOptions[] | undefined;
}
const SelectOption = Select.Option;

export type CustomFieldsType = Omit<FieldsType, 'value'>;

export const formatFieldsValue = (fields: Record<string, any>[]) => fields.map(item => ({
  name: item.name,
  value: item.value,
  label: item.label,
})).filter(item => item.name !== 'title_prefix');

export const getLocalTapd = () => {
  try {
    return JSON.parse(storageGet(`${getProductId()}-tapd`)!);
  } catch (e) {
    return null;
  }
};

export const formatCustomFields = (
  customFields: CustomFieldsType[],
  appVersionOptions: Option[],
) => customFields.map((field: any) => {
  if (field.name === initVersionReportData.name) {
    const options = appVersionOptions?.reduce?.<Option[]>((prev, current) => {
      const target = prev.find((item: any) => item.value === current.value);
      if (!target) {
        prev.push({ ...current, value: current.label });
      }
      return prev;
    }, []);
    return {
      ...field,
      type: 'autoComplete',
      options,
    };
  }
  if (field.name === initReporterData.name || field.name === initCurrentOwnerData.name) {
    return {
      ...field,
      type: 'userSelect',
    };
  }
  return field;
});

export const CreateTapdModal: FC<{
  type: Zone,
  title: string,
  content: string,
  issueKey: string,
  issueUrl: string,
  pageId: string,
  defaultAppVersion?: string,
  appVersionOptions: Option[],
  defaultTapdHandler?: string;
  visible: boolean,
  onClose: () => void,
  onChangeId: (workspaceId: string) => void,
  openEidtModal: (editType: EditType, mode: string) => void,
  onSubmit?: () => void,
}> = observer(({
  type,
  title,
  content,
  issueKey,
  issueUrl,
  pageId,
  appVersionOptions,
  defaultTapdHandler,
  visible,
  onClose,
  onChangeId,
  openEidtModal,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { tapdList } = useStores().TapdStore;

  const initStatusParams: CreateTapdIssueReq = {
    product_id: '',
    type: getTypeByZone(type),
    monitor_type: type,
    issue_key: issueKey,
    work_space_id: '',
    field_values: [],
  };
  const [loading, setLoading] = useState(false);
  const [statusVisible, setStatusVisible] = useState(false);
  const [tapdLists, setTapdLists] = useState<FilterOptions[]>([]);
  const [statusParams, setStatusParams] = useState(initStatusParams);
  const initTitlePrefix = t("【Bugly-性能监控】");
  const [tapdTitle, setTapdTitle] = useState('');
  const [fields, setFields] = useState<FieldsType[]>(getInitFields(appVersionOptions));
  const [tapdTemplateInfos, setTapdTemplateInfos] = useState<ITapdTemplateInfos[]>([]);
  const [mode, setMode] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [innerContent, setInnerContent] = useState(content);

  const handleSelectChange = (val: string | number, key?: string) => {
    setStatusParams({ ...statusParams, [key as string]: val });
    if (onChangeId) onChangeId(val as string);
  };

  const handleSubmit = async () => {
    if (tapdTitle.length > 100) {
      message.warning(t("标题长度不能超过100，请修改后再提交"));
      return;
    }
    if (!statusParams.work_space_id) {
      message.warning(t("请选择TAPD项目"));
      return;
    }
    setLoading(true);
    const res = await createTapdIssue({
      workSpaceId: statusParams.work_space_id,
      type: statusParams.type,
      productId: statusParams.product_id,
      issueKey: statusParams.issue_key,
      issueType: statusParams.type,
      fieldValues: [
        ...formatFieldsValue(fields),
        {
          name: 'title',
          label: t("标题"),
          value: `${tapdTitle}`,
        },
        {
          name: 'description',
          label: t("详细描述"),
          value: t('{{content}}<a href=\'{{url}}&tapd=true\'>&gt;&gt;&gt;点击查看详情&lt;&lt;&lt;</a>', { content: innerContent, url: issueUrl }).replace(/\n/g, '<br>'),
        },
      ],
    });
    if (res) {
      setStatusVisible(false);
      onClose();
      if (onSubmit) {
        onSubmit();
      }
    }
    setLoading(false);
    reportClick(pageId, CUSTOM_ACTIONS.commonCreateTapdBug, { from: pageId });
  };

  const loadTapdFields = async ()  => {
    setLoading(true);
    const customFields = await getCustomFields(statusParams.work_space_id);
    const newCustomFields = formatCustomFields(customFields, appVersionOptions!);
    const tapdTemplateInfos = await getIssueTapdInfos({
      productId: getProductId(),
      workSpaceId: statusParams.work_space_id ?? tapdList[0]?.workspaceId,
    });
    setTapdTemplateInfos(tapdTemplateInfos);
    const tapdInfo = tapdTemplateInfos.find(item => item.templateId === mode) ?? tapdTemplateInfos[0];
    setMode(tapdInfo?.templateId ?? '');
    setIsCreator(tapdInfo?.creator === getUserId());
    storageSet(`${getProductId()}-tapd`, JSON.stringify({ mode, work_space_id: statusParams.work_space_id }));
    const info = formatTapdInfo(tapdInfo?.data ?? [], newCustomFields);
    if (info.newTitlePrefix) {
      setTapdTitle(`${info.newTitlePrefix}${title || ''}`);
    } else {
      setTapdTitle(`${initTitlePrefix}${title || ''}`);
    }
    if (!!tapdInfo?.data?.length) {
      const newFields = info.newFields.map((item) => {
        if (item.name === initCurrentOwnerData.name) {
          return {
            ...item,
            value: defaultTapdHandler || item.value,
          };
        }
        if (item.name === initReporterData.name) {
          return {
            ...item,
            value: item.value || initReporterData.value,
          };
        }
        return item;
      });
      setFields(newFields);
    } else {
      setFields(getInitFields(appVersionOptions));
    }
    setLoading(false);
  };

  const getNewFields = (changeValue: string | number, name?: string | null) => {
    const newFields = fields.map((item: FieldsType) => {
      if (item.name === name) {
        return { ...item, value: changeValue };
      }
      return item;
    });
    setFields(newFields);
  };

  const handleChangeMode = (val: string | number) => setMode(val as string);

  const loadIsAdmin = async () => {
    setLoading(true);
    const isAdmin = await getRoleByUser();
    setIsAdmin(isAdmin);
    setLoading(false);
  };

  const renderItem = (field: FieldsType) => {
    switch (field.type) {
      case 'select':
        return <RenderSelect
          name={field.name}
          label={field.label}
          options={field.options!}
          getNewFields={getNewFields}
          value={field.value}
          handleSelectChange={handleSelectChange}
        />;
      case 'autoComplete':
        return <RenderAutoComplete
          name={field.name}
          label={field.label}
          options={field.options!}
          getNewFields={getNewFields}
          value={field.value}
          handleSelectChange={handleSelectChange}
        />;
      case 'userSelect':
        return <RenderUserSelect
          name={field.name}
          label={field.label}
          getNewFields={getNewFields}
          value={field.value as string}
        />;
      default:
        return <RenderInput
          name={field.name}
          label={field.label}
          value={field.value}
          getNewFields={getNewFields}
        />;
    }
  };

  useEffect(() => {
    setStatusVisible(visible);
    if (visible) {
      const localTapd = getLocalTapd();
      localTapd?.work_space_id && setStatusParams({ ...statusParams, work_space_id: localTapd.work_space_id });
      localTapd?.mode && setMode(localTapd.mode as string);
      loadIsAdmin();
    }
  }, [visible]);

  useEffect(() => {
    const list: FilterOptions[] = [];
    tapdList.forEach((item) => {
      if (item.workspaceName && item.workspaceId) {
        list.push({ label: item.workspaceName, value: item.workspaceId });
      }
    });
    statusParams.product_id = getProductId();
    statusParams.work_space_id = tapdList[0]?.workspaceId || '';
    setTapdLists(list);
  }, [tapdList]);

  useEffect(() => {
    if (statusParams.work_space_id && statusVisible) {
      loadTapdFields();
    }
  }, [statusParams.work_space_id, statusVisible, mode, title]);

  useEffect(() => {
    setInnerContent(content);
  }, [content]);


  const deleteTemplate =  (item: ITapdTemplateInfos) => {
    setStatusVisible(false);
    ModalMsg({
      type: ModalType.CONFIRM,
      typeIcon: ModalIconType.INFO,
      content: t('你确认要删除缺陷模板 {{name}} ({{id}})吗？', { name: item.templateName, id: item.templateId }),
      cancelText: t("取消"),
      title: t("删除缺陷模板"),
      okText: t("确认"),
      onCancel: () => {
        setStatusVisible(true);
      },
      onOk: () => new Promise<void>((resolve, reject) => {
        deleteIssueTapdInfos({ templateId: item.templateId }).then((rsp) => {
          if (rsp) {
            message.success(t("删除成功!"));
            resolve();
            setTapdTemplateInfos(infos => infos.filter(info => item.templateId !== info.templateId));
            setStatusVisible(true);
          } else {
            reject();
          }
        });
      }),
    });
  };

  const tapdTemplateInfosOptions = () => {
    const userId = getUserId();
    return tapdTemplateInfos.map(item => (
      <SelectOption key={item.templateId} value={item.templateId} label={`${item.templateName} (${item.templateId})`}>
        <div className={styles.templateInfosOptions}>
        <span role="img" aria-label="China">
         {item.templateName} ({item.templateId})
        </span>
          {isAdmin || item.creator === userId ? <CloseLine onClick={(e: { preventDefault: () => void;
            stopPropagation: () => void; }) => {
            e.preventDefault();
            e.stopPropagation();
            deleteTemplate(item);
          }} className={styles.closeIcon}/> : null}
        </div>
      </SelectOption>));
  };

  return (<>
      <Modal
        visible={statusVisible}
        title={<div style={{
          fontFamily: 'PingFang SC',
          fontWeight: 500,
          fontSize: '16px',
        }}>{t("创建缺陷")}</div>}
        centered
        footer={null}
        onCancel={() => {
          setStatusVisible(false);
          onClose();
        }}
        style={{ minWidth: '1100px' }}
      >
        <Spin spinning={loading}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <RenderSelect
              name={'work_space_id'}
              label={t("关联TAPD")}
              options={tapdLists}
              getNewFields={getNewFields}
              value={statusParams.work_space_id ?? tapdList[0]?.workspaceId}
              handleSelectChange={handleSelectChange}
              labelWidth={'100px'}
              renderSelectWidth={'902px'}
              style={{ width: '794px' }}
            />
          </div>
          <div style={{ display: 'flex' }}>
            <RenderSelect
              name={'mode'}
              label={t("缺陷模板")}
              getNewFields={getNewFields}
              value={mode || tapdTemplateInfos[0]?.templateId || ''}
              handleSelectChange={handleChangeMode}
              labelWidth={'100px'}
              renderSelectWidth={'620px'}
              style={{ width: '510px' }}
              selectProps={{
                optionLabelProp: 'label',
              }}
            >{tapdTemplateInfosOptions()}</RenderSelect>
            <Button
              style={{
                color: (!isAdmin && !isCreator) || !tapdTemplateInfos.length ? '#D1D1D4' : '#1573ff',
                textAlign: 'center',
                fontSize: '16px',
                lineHeight: '20px',
                marginLeft: '12px',
              }}
              disabled={(!isAdmin && !isCreator) || !tapdTemplateInfos.length}
              onClick={() => {
                if (!statusParams.work_space_id) {
                  message.warning(t("请选择TAPD项目，再编辑模板"));
                  return;
                }
                setStatusVisible(false);
                onClose();
                openEidtModal('update', mode || tapdTemplateInfos[0]?.templateId);
                onChangeId(statusParams.work_space_id);
              }}>
              <img src={(!isAdmin && !isCreator) || !tapdTemplateInfos.length ? CreateTapdEditDisable : CreateTapdEdit} style={{ marginRight: '12px' }} alt={''}/>
              {t("编辑模板")}
            </Button>
            <Button
              style={{
                color: '#0A6CFF',
                textAlign: 'center',
                fontSize: '16px',
                lineHeight: '20px',
                marginLeft: '12px',
              }}
              onClick={() => {
                if (!statusParams.work_space_id) {
                  message.warning(t("请选择TAPD项目，再创建新模板"));
                  return;
                }
                setStatusVisible(false);
                onClose();
                openEidtModal('add', '');
                onChangeId(statusParams.work_space_id);
              }}>
              <PlusCircleOutlined style={{ marginRight: '1px' }}/>
              {t("创建新模板")}
            </Button>
          </div>
          <div style={{ display: 'flex' }}>
            <span style={{ flexShrink: 0, lineHeight: '30px', marginRight: '12px', width: '100px', textAlign: 'right' }}>{t("标题")}</span>
            <Input
              value={`${tapdTitle}`}
              onChange={e => setTapdTitle(e.target.value)}
            />
          </div>
          <div style={{ marginBottom: '20px', marginTop: '30px', fontSize: '16px', fontWeight: 500, fontFamily: 'PingFang SC' }}>
            {t("基本信息")}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {fields.map(field => renderItem(field))}
          </div>
          <div>
            <div style={{ marginBottom: '20px', marginTop: '20px', fontSize: '14px', fontWeight: 500, fontFamily: 'PingFang SC' }}>{t("缺陷内容")}</div>
            <Input.TextArea
              value={innerContent}
              style={{ minHeight: '200px' }}
              onChange={(e) => {
                setInnerContent(e.target.value);
              }}
            />
          </div>
          <div style={{ textAlign: 'right', marginTop: '40px' }}>
            <Button onClick={() => {
              setStatusVisible(false);
              onClose();
            }}>
              {t("取消")}
            </Button>
            <Button type='primary' onClick={handleSubmit} style={{ marginLeft: '20px' }}>
              {t("确定")}
            </Button>
          </div>
        </Spin>
      </Modal>
    </>
  );
});
